const TERMINAL_TRANSFER_STATUSES = new Set([
  'available',
  'completed',
  'completed_no_undo',
  'undone',
]);

const COMMITTED_TRANSFER_STATUSES = new Set([
  'available',
  'completed',
  'completed_no_undo',
]);

const RECOVERY_TRANSFER_STATUSES = new Set([
  'pending',
  'undoing',
  'needs_review',
  'unknown',
]);

const durableReceiptId = value => (
  /^[a-f0-9]{32}$/.test(String(value || '').trim().toLowerCase())
);

const normalizedTransferStatus = result => String(result?.status || '')
  .trim()
  .toLowerCase();

/**
 * Interpret only the durable transfer state as proof of a committed copy/move.
 * Planned item arrays on an unsuccessful response are deliberately ignored.
 */
export const getDocumentTransferOutcome = (result, isCopy) => {
  const status = normalizedTransferStatus(result);
  const receiptId = String(result?.receipt_id || '').trim().toLowerCase();
  const hasDurableReceipt = durableReceiptId(receiptId);
  const hasRecoverySignal = result?.recovery_required === true
    || result?.can_resume === true
    || RECOVERY_TRANSFER_STATUSES.has(status);
  const recoveryRequired = hasDurableReceipt && hasRecoverySignal;
  const outcomeUnknown = result?.outcome_unknown === true
    || (hasRecoverySignal && !hasDurableReceipt);
  const terminal = result?.success === true
    && !hasRecoverySignal
    && (status === '' || TERMINAL_TRANSFER_STATUSES.has(status));
  const undone = terminal && status === 'undone';
  const committed = terminal
    && !undone
    && (status === '' || COMMITTED_TRANSFER_STATUSES.has(status));
  const committedRows = committed && Array.isArray(isCopy ? result?.copied : result?.moved)
    ? (isCopy ? result.copied : result.moved)
    : [];
  const errors = Array.isArray(result?.errors) ? result.errors : [];

  return {
    successful: committed,
    terminal,
    undone,
    committed,
    committedCount: committedRows.length,
    errorCount: errors.length,
    skippedCount: Math.max(0, Number(result?.skipped_count || 0)),
    recoveryRequired,
    canResume: recoveryRequired && result?.can_resume === true,
    needsReview: recoveryRequired && status === 'needs_review',
    outcomeUnknown,
    definitiveRejection: !terminal && !recoveryRequired && !outcomeUnknown,
    partial: false,
    receiptId: hasDurableReceipt ? receiptId : '',
    status,
  };
};

export const getDocumentTransferRecoveryMessage = (result, isCopy) => {
  const operation = result?.durable_file_rename === true
    ? 'rename'
    : (isCopy ? 'copy' : 'move');
  const outcome = getDocumentTransferOutcome(result, isCopy);
  if (outcome.canResume) {
    return `The ${operation} request was saved but has not finished. Resume it from Recent document actions.`;
  }
  if (outcome.needsReview) {
    return `The ${operation} request was saved but needs review. Open Recent document actions to check its status.`;
  }
  if (outcome.recoveryRequired) {
    return `The ${operation} request was saved but has not finished. Open Recent document actions to check its status.`;
  }
  if (outcome.outcomeUnknown) {
    return `CaseDB could not confirm whether the ${operation} request finished. Retry the same selection to safely check it; CaseDB will reuse the same request.`;
  }
  return String(result?.message || result?.error || `The ${operation} failed.`);
};

const normalizeIntentKeys = values => Array.from(new Set(
  (Array.isArray(values) ? values : [])
    .map(value => String(value || '').trim())
    .filter(Boolean)
)).sort((left, right) => left.localeCompare(right));

const normalizeIntentRenameMap = renameMap => Object.keys(
  renameMap && typeof renameMap === 'object' ? renameMap : {}
).sort((left, right) => left.localeCompare(right)).reduce((normalized, key) => {
  normalized[String(key).trim()] = String(renameMap[key] || '').trim();
  return normalized;
}, {});

/** Build a stable browser-only key for one canonical copy/move intent. */
export const buildDocumentTransferIntentKey = ({
  operation,
  caseId,
  userId = '',
  sourceKeys,
  targetFolder,
  renameMap = {},
}) => JSON.stringify({
  operation: operation === 'copy' ? 'copy' : 'move',
  case_id: String(caseId ?? '').trim(),
  user_id: String(userId ?? '').trim(),
  source_keys: normalizeIntentKeys(sourceKeys),
  target_folder: String(targetFolder || '').trim().replace(/^\/+|\/+$/g, ''),
  rename_map: normalizeIntentRenameMap(renameMap),
});
